import React, { useState } from 'react';
import { AddToolModal } from './AddToolModal';
import { ResetModal } from './ResetModal';
import { TemplatesDropdown } from './TemplatesDropdown';
import { GlobalTickerInput } from './GlobalTickerInput';
import { useTickerLink } from '../contexts/TickerLinkContext';
import { SqueezoGauge } from './SqueezoGauge';
import { DashboardTabs } from './shell/DashboardTabs';

export const DashboardToolbar: React.FC = () => {
  const [isAddToolModalOpen, setIsAddToolModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isTickerSettingsOpen, setIsTickerSettingsOpen] = useState(false);
  const { autoFocusEnabled, setAutoFocusEnabled } = useTickerLink();

  return (
    <>
      <div className="bg-dark-surface border-b border-dark-border h-10 flex items-center px-4 gap-2">
        {/* Dashboard tabs replace the old selector dropdown */}
        <DashboardTabs />

        {/* Spacer — actions are right-aligned per the shell mockup */}
        <div className="flex-1" />

        {/* Squeezo Gauge */}
        <SqueezoGauge />

        {/* Separator */}
        <div className="w-px h-5 bg-dark-border mx-1 flex-shrink-0" />

        {/* Ticker Link: global input + its settings. This is the live linking
            system (widgets opt in via the link icon in their own headers) —
            it replaces the one-shot Sync Tickers modal, which had been dead
            code and is no longer surfaced. */}
        <GlobalTickerInput />

        {/* Ticker Settings */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setIsTickerSettingsOpen(!isTickerSettingsOpen)}
            className={`p-1 rounded transition-colors ${
              isTickerSettingsOpen
                ? 'text-white bg-dark-surface-light'
                : 'text-gray-400 hover:text-gray-300 hover:bg-dark-surface-light'
            }`}
            title="Ticker link settings"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>

          {/* Settings dropdown */}
          {isTickerSettingsOpen && (
            <>
              {/* Backdrop to close on click outside */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsTickerSettingsOpen(false)}
              />
              <div className="absolute top-full right-0 mt-1 w-56 bg-dark-surface border border-dark-border rounded shadow-lg z-50 py-2">
                <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-dark-surface-light cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoFocusEnabled}
                    onChange={(e) => setAutoFocusEnabled(e.target.checked)}
                    className="rounded border-dark-border bg-dark-surface text-bull-blue focus:ring-accent-yellow focus:ring-offset-0"
                  />
                  <span className="text-xs text-gray-300">Auto-focus on typing</span>
                </label>
                <p className="px-3 py-1 text-[10px] text-gray-500">
                  When enabled, typing anywhere focuses the ticker input
                </p>
              </div>
            </>
          )}
        </div>

        {/* Separator */}
        <div className="w-px h-5 bg-dark-border mx-1 flex-shrink-0" />

        {/* Dashboard-scoped actions */}
        <button
          data-tour="add-tool"
          onClick={() => setIsAddToolModalOpen(true)}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-accent-yellow hover:bg-wheat text-hot-tag-text rounded transition-colors flex-shrink-0"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Tool
        </button>

        <TemplatesDropdown />

        <button
          onClick={() => setIsResetModalOpen(true)}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-300 bg-dark-bg hover:bg-dark-surface-light border border-dark-border rounded transition-colors flex-shrink-0"
          title="Reset dashboard"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
          </svg>
          Reset
        </button>

        {/* Tour Replay — kept because DashboardPage still listens for
            restart-tour; Joe's toolbar drops this, which would orphan the
            listener. Scope is this file only, so we cannot remove the listener. */}
        <button
          onClick={() => {
            const event = new CustomEvent('restart-tour');
            window.dispatchEvent(event);
          }}
          className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-dark-surface-light transition-colors flex-shrink-0"
          title="Take the tour"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      </div>

      {/* Modals */}
      <AddToolModal isOpen={isAddToolModalOpen} onClose={() => setIsAddToolModalOpen(false)} />
      <ResetModal isOpen={isResetModalOpen} onClose={() => setIsResetModalOpen(false)} />
    </>
  );
};
